import React from "react";
import { useNavigate } from "react-router-dom";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 5, 2025";

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2" onClick={() => navigate("/")} style={{cursor: 'pointer'}}>
            <Settings className="h-8 w-8 text-slate-900" />
            <span className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")}>Home</Button>
            <Button variant="ghost" onClick={() => navigate("/terms")}>Terms of Service</Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-medium text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Privacy Policy</h1>
        <p className="text-slate-500 mb-12">Last updated: {lastUpdated}</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Instructing.com, LLC ("Company," "we," "us," or "our") operates Certificate Machine (the "Service") at certificatemachine.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By using Certificate Machine, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-medium text-slate-800 mb-3">2.1 Information You Provide</h3>
            <p className="text-slate-600 leading-relaxed mb-4">When you register for an account or use our Service, we may collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>Account Information:</strong> Name, email address, organization name, and password</li>
              <li><strong>Certificate Data:</strong> Information you include in certificates such as recipient names, email addresses, certificate titles, descriptions, and dates</li>
              <li><strong>Payment Information:</strong> When you make a purchase, payment is processed by Stripe. We do not store your full credit card number. We receive transaction confirmations and subscription status from Stripe.</li>
              <li><strong>Communications:</strong> Information you provide when you contact us for support</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-800 mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-slate-600 leading-relaxed mb-4">When you access our Service, we automatically collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>Usage Data:</strong> Pages visited, features used, certificate views and downloads</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
              <li><strong>Log Data:</strong> IP address, access times, referring URLs</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-800 mb-3">2.3 Information from Third Parties</h3>
            <p className="text-slate-600 leading-relaxed">
              If you purchase through AppSumo, we receive your license code and email address to activate your account. We may also receive information from authentication providers if you use third-party login options.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide, maintain, and improve our Service</li>
              <li>Create and manage your account</li>
              <li>Process transactions and send related information</li>
              <li>Send certificates to recipients on your behalf</li>
              <li>Provide public verification pages for issued certificates</li>
              <li>Send administrative emails (account confirmations, security alerts, updates)</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. How We Share Your Information</h2>
            <p className="text-slate-600 leading-relaxed mb-4">We may share your information in the following circumstances:</p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.1 Service Providers</h3>
            <p className="text-slate-600 leading-relaxed mb-4">We use third-party services to operate our business:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Resend:</strong> Email delivery for certificates and notifications</li>
              <li><strong>MongoDB Atlas:</strong> Database hosting</li>
              <li><strong>AppSumo:</strong> License management (if applicable)</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.2 Public Certificate Information</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              When you issue a certificate, certain information becomes publicly accessible on the verification page, including: recipient name, certificate title, issuing organization name, issue date, and certificate ID. This is a core feature of the Service to enable third-party verification.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.3 Legal Requirements</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              We may disclose your information if required by law or in response to valid requests by public authorities (e.g., court order, government request).
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">4.4 Business Transfers</h3>
            <p className="text-slate-600 leading-relaxed">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any change in ownership or use of your information.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We retain your information for as long as your account is active or as needed to provide you services. Specifically:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Account Data:</strong> Retained until you delete your account</li>
              <li><strong>Certificate Data:</strong> Retained indefinitely to support ongoing verification (certificates are meant to be permanent records)</li>
              <li><strong>Usage Logs:</strong> Retained for up to 12 months</li>
              <li><strong>Payment Records:</strong> Retained as required by tax and accounting laws (typically 7 years)</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your information, including encryption of data in transit (HTTPS/TLS), secure password hashing, access controls, and regular security assessments. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Your Rights and Choices</h2>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.1 Account Information</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              You may update, correct, or delete your account information at any time through your account settings or by contacting us.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.2 Data Export</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              You may request a copy of your data by contacting us at the email address below.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.3 Account Deletion</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              You may request deletion of your account by contacting us. Note that certificates you have issued will remain accessible for verification purposes, as they serve as permanent credentials for recipients.
            </p>

            <h3 className="text-xl font-medium text-slate-800 mb-3">7.4 Email Communications</h3>
            <p className="text-slate-600 leading-relaxed">
              You may opt out of promotional emails by following the unsubscribe instructions in those emails. You cannot opt out of transactional emails related to your account or certificates.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Rights for EEA, UK, and Swiss Users (GDPR)</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you are located in the European Economic Area, United Kingdom, or Switzerland, you have additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Right to Restrict Processing:</strong> Request limitation of processing in certain circumstances</li>
              <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              Our legal basis for processing your information includes: performance of our contract with you, our legitimate business interests, compliance with legal obligations, and your consent where applicable.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. California Privacy Rights (CCPA)</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Right to Know:</strong> Request disclosure of what personal information we collect, use, and share</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt out of the sale of personal information (we do not sell personal information)</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              To exercise these rights, please contact us using the information below. We will verify your identity before responding to your request.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Cookies and Tracking</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Keep you logged in to your account</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our Service</li>
              <li>Improve our Service</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              You can control cookies through your browser settings. Disabling cookies may affect some functionality of the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Our Service is not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16, we will take steps to delete that information.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. International Data Transfers</h2>
            <p className="text-slate-600 leading-relaxed">
              Your information may be transferred to and processed in countries other than your own, including the United States. These countries may have different data protection laws. By using our Service, you consent to the transfer of your information to these countries. We take appropriate safeguards to ensure your information remains protected.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically. Your continued use of the Service after any changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">14. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-700 font-medium">Instructing.com, LLC</p>
              <p className="text-slate-600">Email: cs@instructing.com</p>
              <p className="text-slate-600">Website: certificatemachine.com</p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500">© 2025 Instructing.com, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
